import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, LOCALE_ID, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Params, Router } from '@angular/router';
import { BuilderPrefetchService, BuilderService, GenericLogger } from '@scalefast/ecommerce-core';
import { firstValueFrom } from 'rxjs';
import { BuilderPage, PageComponents } from 'src/app/core/interfaces';
import { PageLayout } from 'src/app/layout/layout.enum';
import { LocalizeHelper } from 'src/app/shared/helpers/localize.helper';
import { ServerNavigateService } from 'src/app/shared/services/server-navigate.service';
import { AbstractPageHandler } from './page-handler-abstract';

@Injectable({ providedIn: 'root' })
export class PageBuilderHandler extends AbstractPageHandler {
  #builderService = inject(BuilderService);
  #locale = inject(LOCALE_ID);
  #router = inject(Router);
  #serverNavigateService = inject(ServerNavigateService);
  #builderPrefetchService = inject(BuilderPrefetchService);
  #logger = inject(GenericLogger);

  override async handle(route: ActivatedRouteSnapshot): Promise<BuilderPage | null> {
    const routeParams = Object.values(route.params);
    const builderData = await this.#getPageData(`/${routeParams.join('/')}`, route.queryParams);
    if (!builderData) {
      return super.handle(route) as Promise<BuilderPage>;
    }
    // The page is loaded with a server navigation when the user accesses it to avoid slow loading times.
    // This reload causes the requested page data to be performed on the server side.
    if (this.#router.navigated && builderData.extraData?.serverNavigation) {
      await this.#serverNavigateService.navigateToRoute(route);
    }
    return builderData as BuilderPage;
  }

  #formatPageData(builderData: any): BuilderPage {
    const data = builderData.page.value.data;
    const metadata = this.#getPageMetadata(data.seoTags);
    const pageData: BuilderPage = {
      pageProvider: 'builder.io',
      name: LocalizeHelper.getLocaleData(data.title, this.#locale),
      type: data.layout,
      pageType: this.#getLayout(data.layout),
      extraData: {
        title: LocalizeHelper.getLocaleData(data.title, this.#locale),
        subCategories: [],
        id: builderData.page.value.id,
        customBreadcrumbs: this.#getBreadCrumbs(data, []),
        path: data.url,
        metadata: metadata,
        categoryBanner: data.categorybanner ?? null,
        components: data.components ? PageComponents.fromBuilder(data.components) : undefined,
        serverNavigation: builderData.serverNavigation ?? null,
      },
    };
    return pageData;
  }

  #getPageMetadata(seoTags: any): any {
    const temporalData = seoTags ? LocalizeHelper.getLocaleData(seoTags, this.#locale) : null;
    if (temporalData) {
      return temporalData.value?.data;
    }
    return temporalData;
  }

  async #getPageData(path: string, queryParams: Params): Promise<BuilderPage | null> {
    const builderParams = this.#builderService.getPageRequestParams(path, queryParams);
    const prefetchedContent = await this.#builderPrefetchService.getPrefetchedPage(builderParams);
    if (prefetchedContent) {
      return this.#formatPageData(prefetchedContent);
    }
    try {
      const info = await firstValueFrom(this.#builderService.getPageByParams(builderParams));
      if (!info) {
        this.#logger.debug({ msg: 'PageBuilderHandler: No page found in Builder with the given path', path });
        return null;
      }
      return this.#formatPageData(info);
    } catch (error: unknown) {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 404) {
          this.#logger.error({
            msg: 'PageBuilderHandler: Unexpected 404 error requesting Builder page for this path. Page Model request failed',
            path,
          });
        }
      } else {
        this.#logger.error({ msg: 'PageBuilderHandler: Unexpected error handling Builder Page', error });
      }
      return null;
    }
  }

  #getLayout(layout: string): PageLayout {
    return PageLayout.Default;
  }

  #getBreadCrumbs(page: any, breadcrumbs: { label: string; path: string }[]): { label: string; path: string }[] {
    if (page && page.breadcrumbs && page.breadcrumbs.parent) {
      this.#getBreadCrumbs(page.breadcrumbs.parent.value.data, breadcrumbs);
    }
    const label =
      page.breadcrumbs && page.breadcrumbs.title
        ? LocalizeHelper.getLocaleData(page.breadcrumbs.title, this.#locale)
        : LocalizeHelper.getLocaleData(page.title, this.#locale);
    const path = page.url;

    breadcrumbs.push({
      label: label,
      path: path,
    });
    return breadcrumbs;
  }
}
